import AdminDashboard from "@/admin/index";
import { AdSenseScript } from "@/components/AdSenseScript";
import { DevDiagnosticBadge } from "@/components/DevDiagnosticBadge";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FirstLoginNicknameCheck } from "@/components/FirstLoginNicknameCheck";
import { Footer } from "@/components/Footer";
import { BottomTab } from "@/components/layout/BottomTab";
import { NewHeader } from "@/components/layout/NewHeader";
import { TopBar } from "@/components/layout/TopBar";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { RegionProvider } from "@/contexts/RegionContext";
import { ZoneProvider } from "@/contexts/ZoneContext";
import { useBreakpoint, useIsMobile } from "@/hooks/useBreakpoint";
import About from "@/pages/About";
import Admin from "@/pages/Admin";
import AdminAds from "@/pages/AdminAds";
import AdminNewArticle from "@/pages/AdminNewArticle";
import AdminUsers from "@/pages/AdminUsers";
import Advertise from "@/pages/Advertise";
import AdvertisePage from "@/pages/AdvertisePage";
import AgentRegistration from "@/pages/AgentRegistration";
import Analytics from "@/pages/Analytics";
import ArticleDetail from "@/pages/ArticleDetail";
import BizPromo from "@/pages/BizPromo";
import Blog from "@/pages/Blog";
import BorrowingCalculator from "@/pages/BorrowingCalculator";
import Business from "@/pages/Business";
import BusinessDirectory from "@/pages/BusinessDirectory";
import BusinessDirectoryDetail from "@/pages/BusinessDirectoryDetail";
import BusinessDirectoryRegister from "@/pages/BusinessDirectoryRegister";
import BusinessClaimStatus from "@/pages/BusinessClaimStatus";
import BusinessManagement from "@/pages/BusinessManagement";
import BusinessInquiriesManagementPage from "@/pages/BusinessInquiriesManagementPage";
import BusinessLandingPage from "@/pages/BusinessLandingPage";
import BusinessProductsManagementPage from "@/pages/BusinessProductsManagementPage";
import BusinessQRPrint from "@/pages/BusinessQRPrint";
import BusinessSubscription from "@/pages/BusinessSubscription";
import CarDetail from "@/pages/CarDetail";
import Careers from "@/pages/Careers";
import CarForm from "@/pages/CarForm";
import CarMarket from "@/pages/CarMarket";
import Category from "@/pages/Category";
import ChatListPage from "@/pages/ChatListPage";
import ChatRoomPage from "@/pages/ChatRoomPage";
import ChatStart from "@/pages/ChatStart";
import CityClassifiedDetail from "@/pages/CityClassifiedDetail";
import CityClassifiedRegister from "@/pages/CityClassifiedRegister";
import EventCreatePage from "@/pages/community/EventCreatePage";
import EventDetailPage from "@/pages/community/EventDetailPage";
import GroupCreatePage from "@/pages/community/GroupCreatePage";
import GroupDetailPage from "@/pages/community/GroupDetailPage";
import GroupSettingsPage from "@/pages/community/GroupSettingsPage";
import GroupsListPage from "@/pages/community/GroupsListPage";
import MyGroupsPage from "@/pages/community/MyGroupsPage";
import PostCreatePage from "@/pages/community/PostCreatePage";
import PostDetailPage from "@/pages/community/PostDetailPage";
import CommunityTalk from "@/pages/CommunityTalk";
import CommunityTalkDetail from "@/pages/CommunityTalkDetail";
import CommunityTalkForm from "@/pages/CommunityTalkForm";
import Copyright from "@/pages/Copyright";
import DealerRegistration from "@/pages/DealerRegistration";
import EditorialPolicy from "@/pages/EditorialPolicy";
import EmailRefuse from "@/pages/EmailRefuse";
import Flashcards from "@/pages/Flashcards";
import ExperienceForm from "@/pages/ExperienceForm";
import Home from "@/pages/Home";
import Jobs from "@/pages/Jobs";
import KakaoCallback from "@/pages/KakaoCallback";
import Lessons from "@/pages/Lessons";
import LocationTerms from "@/pages/LocationTerms";
import Login from "@/pages/Login";
import LoginSuccess from "@/pages/LoginSuccess";
import Market from "@/pages/Market";
import MarketDetail from "@/pages/MarketDetail";
import Marketplace from "@/pages/Marketplace";
import MarketplaceDetail from "@/pages/MarketplaceDetail";
import MarketplaceRegister from "@/pages/MarketplaceRegister";
import MarketRegister from "@/pages/MarketRegister";
import MeetingAlbumPage from "@/pages/MeetingAlbumPage";
import MeetingDetail from "@/pages/MeetingDetail";
import MeetingEventsPage from "@/pages/MeetingEventsPage";
import MeetingForm from "@/pages/MeetingForm";
import MeetingMembersPage from "@/pages/MeetingMembersPage";
import MeetingPostCreatePage from "@/pages/MeetingPostCreatePage";
import MeetingPostDetailPage from "@/pages/MeetingPostDetailPage";
import MeetingPostEditPage from "@/pages/MeetingPostEditPage";
import MeetingPostsPage from "@/pages/MeetingPostsPage";
import Meetings from "@/pages/Meetings";
import MyMarket from "@/pages/MyMarket";
import MyPage from "@/pages/MyPage";
import News from "@/pages/News";
import NicknameSetupPage from "@/pages/NicknameSetupPage";
import Notifications from "@/pages/Notifications";
import NotFound from "@/pages/not-found";
import Partnership from "@/pages/Partnership";
import Policy from "@/pages/Policy";
import Pricing from "@/pages/Pricing";
import Privacy from "@/pages/Privacy";
import Profile from "@/pages/Profile";
import QuestionForm from "@/pages/QuestionForm";
import PropertyDetail from "@/pages/PropertyDetail";
import PropertyForm from "@/pages/PropertyForm";
import PropertyInspection from "@/pages/PropertyInspection";
import PropertyMarket from "@/pages/PropertyMarket";
import Rankings from "@/pages/Rankings";
import RealEstate from "@/pages/RealEstate";
import RegionalNews from "@/pages/RegionalNews";
import RentOrBuyCalculator from "@/pages/RentOrBuyCalculator";
import Sales from "@/pages/Sales";
import Search from "@/pages/Search";
import ServiceRequestHub from "@/pages/ServiceRequestHub";
import Services from "@/pages/Services";
import Shipping from "@/pages/Shipping";
import Subscribe from "@/pages/Subscribe";
import Support from "@/pages/Support";
import TagPage from "@/pages/TagPage";
import Team from "@/pages/Team";
import Terms from "@/pages/Terms";
import TopicDetail from "@/pages/TopicDetail";
import Topics from "@/pages/Topics";
import UserProtection from "@/pages/UserProtection";
import VerificationInfo from "@/pages/VerificationInfo";
import Vocabulary from "@/pages/Vocabulary";
import Youth from "@/pages/Youth";
import YouTubePage from "@/pages/YouTube";
import YouTubeDetail from "@/pages/YouTubeDetail";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Redirect, Route, Switch } from "wouter";
import { queryClient } from "./lib/queryClient";

function Router() {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";

  return (
    <div className="min-h-screen flex flex-col">
      {/* TopBar는 모바일에서만 표시 (데스크톱은 Header에 통합됨) */}
      {isMobile && <TopBar />}

      <div className={`flex-1 ${isMobile ? "pb-14" : ""}`}>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/chat" component={ChatListPage} />
          <Route path="/chat-start/:type/:id" component={ChatStart} />
          <Route path="/chat/:chatId" component={ChatRoomPage} />
          <Route path="/market" component={Market} />
          <Route path="/unified-market">
            <Redirect to="/market" />
          </Route>
          <Route path="/news" component={News} />
          <Route path="/article/:id" component={ArticleDetail} />
          <Route path="/category/:id" component={Category} />
          <Route path="/tag/:tag" component={TagPage} />
          <Route path="/topics" component={Topics} />
          <Route path="/topics/:topicId" component={TopicDetail} />
          <Route path="/search" component={Search} />
          <Route path="/vocabulary" component={Vocabulary} />
          <Route path="/flashcards" component={Flashcards} />
          <Route path="/analytics" component={Analytics} />
          <Route path="/profile" component={Profile} />
          <Route path="/mypage" component={MyPage} />
          <Route path="/notifications" component={Notifications} />
          <Route path="/pricing" component={Pricing} />
          <Route path="/subscribe" component={Subscribe} />
          <Route path="/admin/legacy" component={Admin} />
          <Route path="/admin/new" component={AdminNewArticle} />
          <Route path="/admin/edit/:id" component={AdminNewArticle} />
          {/* /admin/ads used to be claimed here, ahead of /admin/:rest* below,
              so clicking "Ads" in the new AdminDashboard shell (client/src/admin/index.tsx,
              which nests its own /admin/ads -> AdsManagement.tsx) actually bounced
              out to this older standalone page and lost the admin nav bar.
              Moved to its own path so both stay reachable: /admin/ads now goes
              to the AdminDashboard tab as the nav expects, and this richer
              legacy page (edit/delete, analytics chart, invoice generator)
              is still available directly at /admin/ads-legacy. */}
          <Route path="/admin/ads-legacy" component={AdminAds} />
          <Route path="/admin/users" component={AdminUsers} />
          <Route path="/admin" component={AdminDashboard} />
          <Route path="/admin/:rest*" component={AdminDashboard} />
          <Route path="/terms" component={Terms} />
          <Route path="/privacy" component={Privacy} />
          <Route path="/policy" component={Policy} />
          <Route path="/location-terms" component={LocationTerms} />
          <Route path="/user-protection" component={UserProtection} />
          <Route path="/youth" component={Youth} />
          <Route path="/copyright" component={Copyright} />
          <Route path="/editorial-policy" component={EditorialPolicy} />
          <Route path="/email-refuse" component={EmailRefuse} />
          <Route path="/about" component={About} />
          <Route path="/services" component={Services} />
          <Route path="/team" component={Team} />
          <Route path="/blog" component={Blog} />
          <Route path="/careers" component={Careers} />
          <Route path="/business" component={Business} />
          <Route path="/partnership" component={Partnership} />
          <Route path="/advertise" component={Advertise} />
          <Route path="/support" component={Support} />
          <Route path="/business/advertise" component={AdvertisePage} />
          <Route path="/marketplace" component={Marketplace} />
          <Route path="/marketplace/register" component={MarketplaceRegister} />
          <Route path="/marketplace/:id" component={MarketplaceDetail} />
          <Route path="/business-directory" component={BusinessDirectory} />
          <Route
            path="/business-directory/register"
            component={BusinessDirectoryRegister}
          />
          <Route
            path="/business-directory/:id/edit"
            component={BusinessDirectoryRegister}
          />
          <Route
            path="/business-directory/:id/claim"
            component={BusinessClaimStatus}
          />
          <Route
            path="/business-directory/:id"
            component={BusinessDirectoryDetail}
          />
          <Route path="/regional-news" component={RegionalNews} />
          <Route path="/rankings" component={Rankings} />
          <Route path="/youtube" component={YouTubePage} />
          <Route path="/youtube/:id" component={YouTubeDetail} />
          <Route path="/market/register" component={MarketRegister} />
          <Route path="/create">
            <Redirect to="/questions/new" />
          </Route>
          <Route path="/market/edit/:id" component={MarketRegister} />
          <Route path="/market/my" component={MyMarket} />
          <Route path="/market/:id" component={MarketDetail} />
          {/* Redirect /car to /cars for SEO and user convenience */}
          <Route path="/car">
            <Redirect to="/cars" />
          </Route>
          <Route path="/cars" component={CarMarket} />
          <Route path="/cars/new" component={CarForm} />
          <Route path="/cars/edit/:id" component={CarForm} />
          <Route path="/cars/dealer/register" component={DealerRegistration} />
          <Route path="/cars/:id" component={CarDetail} />
          <Route path="/real-estate" component={RealEstate} />
          <Route path="/properties" component={PropertyMarket} />
          <Route path="/properties/new" component={PropertyForm} />
          <Route path="/properties/edit/:id" component={PropertyForm} />
          <Route
            path="/properties/agent/register"
            component={AgentRegistration}
          />
          <Route path="/properties/:id" component={PropertyDetail} />
          <Route
            path="/properties/:id/inspection"
            component={PropertyInspection}
          />
          <Route path="/tools/rent-or-buy" component={RentOrBuyCalculator} />
          <Route
            path="/tools/borrowing-calculator"
            component={BorrowingCalculator}
          />
          <Route path="/lessons" component={Lessons} />
          <Route path="/jobs" component={Jobs} />
          <Route path="/shipping" component={Shipping} />
          <Route path="/biz" component={BizPromo} />
          <Route path="/biz/:slug" component={BusinessLandingPage} />
          <Route path="/biz/:slug/qr-print" component={BusinessQRPrint} />
          <Route path="/service-requests" component={ServiceRequestHub} />
          <Route
            path="/businesses/:id/manage"
            component={BusinessManagement}
          />
          <Route
            path="/businesses/:id/products"
            component={BusinessProductsManagementPage}
          />
          <Route
            path="/businesses/:id/inquiries"
            component={BusinessInquiriesManagementPage}
          />
          <Route
            path="/businesses/:id/subscription"
            component={BusinessSubscription}
          />
          <Route path="/sales" component={Sales} />
          <Route
            path="/city-classifieds/register"
            component={CityClassifiedRegister}
          />
          <Route
            path="/city-classifieds/:id"
            component={CityClassifiedDetail}
          />
          <Route path="/verification-info" component={VerificationInfo} />
          <Route path="/community-talk" component={CommunityTalk} />
          <Route path="/questions/new" component={QuestionForm} />
          <Route path="/experiences/new" component={ExperienceForm} />
          <Route path="/community-talk/new">
            <CommunityTalkForm />
          </Route>
          <Route path="/community-talk/edit/:id">
            <CommunityTalkForm />
          </Route>
          <Route path="/community-talk/:id" component={CommunityTalkDetail} />
          <Route path="/meetings" component={Meetings} />
          <Route path="/meetings/new" component={MeetingForm} />
          <Route path="/meetings/edit/:id" component={MeetingForm} />
          <Route path="/meetings/:id/album" component={MeetingAlbumPage} />
          <Route path="/meetings/:id/members" component={MeetingMembersPage} />
          <Route path="/meetings/:id/events" component={MeetingEventsPage} />
          <Route path="/meetings/:id/posts" component={MeetingPostsPage} />
          {/* /posts/new와 /posts/:postId/edit는 /posts/:postId보다 먼저 매칭되어야 함 */}
          <Route
            path="/meetings/:id/posts/new"
            component={MeetingPostCreatePage}
          />
          <Route
            path="/meetings/:id/posts/:postId/edit"
            component={MeetingPostEditPage}
          />
          <Route
            path="/meetings/:id/posts/:postId"
            component={MeetingPostDetailPage}
          />
          <Route path="/meetings/:id" component={MeetingDetail} />
          {/* Community Groups */}
          <Route path="/community/groups" component={GroupsListPage} />
          <Route path="/community/groups/new" component={GroupCreatePage} />
          <Route
            path="/community/groups/:groupId/settings"
            component={GroupSettingsPage}
          />
          <Route
            path="/community/groups/:groupId"
            component={GroupDetailPage}
          />
          <Route
            path="/community/groups/:groupId/posts/new"
            component={PostCreatePage}
          />
          <Route
            path="/community/groups/:groupId/events/new"
            component={EventCreatePage}
          />
          <Route
            path="/community/events/:eventId"
            component={EventDetailPage}
          />
          <Route path="/community/posts/:postId" component={PostDetailPage} />
          <Route path="/my-groups" component={MyGroupsPage} />
          <Route path="/auth/kakao/callback" component={KakaoCallback} />
          <Route path="/login/success" component={LoginSuccess} />
          <Route path="/login" component={Login} />
          <Route path="/nickname/setup" component={NicknameSetupPage} />
          <Route component={NotFound} />
        </Switch>
      </div>

      {/* Phase 7.1: Show BottomTab on mobile only */}
      {isMobile && <BottomTab />}
    </div>
  );
}

function App() {
  const isMobile = useIsMobile();

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AuthProvider>
              <RegionProvider>
                <ZoneProvider>
                  <LocationProvider>
                    <TooltipProvider>
                      <AdSenseScript />
                      <DevDiagnosticBadge />
                      <div className="min-h-screen bg-black">
                        {/* 뉴스 스타일 Header (데스크톱 전용) */}
                        {!isMobile && <NewHeader />}
                        <Router />
                        {!isMobile && <Footer />}
                      </div>
                      <Toaster />
                      {/* Check for nickname on first login */}
                      <FirstLoginNicknameCheck />
                    </TooltipProvider>
                  </LocationProvider>
                </ZoneProvider>
              </RegionProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
